import React, { useState, useEffect, useRef } from 'react';
import JournalModal from '../components/JournalModal';
import { journalEntries } from '../data/journalEntries';
import SEO from '../components/SEO';
import './Journal.css';

const Journal = () => {
  const [selectedTag, setSelectedTag] = useState('All');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const filterRef = useRef(null);

  const tags = ['All', 'Process', 'Poem', 'Behind the Canvas', 'Prayer'];
  
  const tagPhotos = {
    'Process': '/images/site-photos/Cool-hands.png',
    'Poem': '/images/site-photos/BR1A2117.JPG',
    'Behind the Canvas': '/images/site-photos/BR1A2131.JPG',
    'Prayer': '/images/site-photos/BR1A1057.JPG'
  };

  const journalPosts = journalEntries;

  const filteredPosts = selectedTag === 'All' 
    ? journalPosts 
    : journalPosts.filter(post => post.tag === selectedTag);

  const handleFilterSelect = (tag) => {
    setSelectedTag(tag);
    setIsFilterOpen(false);
  };

  const handlePostClick = (post) => {
    setSelectedPost(post);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPost(null);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);



  return (
    <div className="journal">
      <SEO 
        title="Journal"
        description="Thoughts, prayers, and behind-the-scenes glimpses into my creative process. Read my latest reflections and artistic journey."
        image="/images/site-photos/BR1A2131.JPG"
        url="/journal"
      />
      <div className="container">
        {/* Header */}
        <div className="journal-header">
          <h1 className="journal-title">STUDIO <span className="brick-font">Journal</span></h1>
          <p className="journal-subtitle">
            Thoughts, prayers, and behind-the-scenes glimpses into my creative process.
          </p>
        </div>

        {/* Tag Filters with Photos */}
        <div className="tag-filters">
          {/* Desktop: Show all filters with photos */}
          <div className="tag-filters-desktop">
            <button
              className={`tag-btn ${selectedTag === 'All' ? 'active' : ''}`}
              onClick={() => setSelectedTag('All')}
            >
              All
            </button>
            {tags.filter(tag => tag !== 'All').map((tag) => (
              <div className="tag-photo-container" key={tag}>
                <img 
                  src={tagPhotos[tag]} 
                  alt={tag} 
                  className={`tag-photo ${selectedTag === tag ? 'active' : ''}`}
                />
                <button
                  className={`tag-btn ${selectedTag === tag ? 'active' : ''}`}
                  onClick={() => setSelectedTag(tag)}
                >
                  {tag}
                </button>
              </div>
            ))}
          </div>

          {/* Mobile: Show current filter with dropdown button */}
          <div className="tag-filters-mobile" ref={filterRef}>
            <div className="mobile-filter-container">
              {/* Show photo to the left when filter is selected */}
              {selectedTag !== 'All' && tagPhotos[selectedTag] && (
                <img 
                  src={tagPhotos[selectedTag]} 
                  alt={selectedTag} 
                  className="mobile-filter-photo"
                />
              )}
              
              <button
                className={`mobile-filter-btn ${selectedTag === 'All' ? 'active' : ''}`}
                onClick={() => handleFilterSelect('All')}
              >
                {selectedTag}
              </button>
              <button
                className={`mobile-filter-dropdown-btn ${isFilterOpen ? 'open' : ''}`}
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                aria-label="Open filter options"
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            
            {isFilterOpen && (
              <div className="mobile-filter-dropdown">
                {tags.filter(tag => tag !== selectedTag).map((tag) => (
                  <button
                    key={tag}
                    className="mobile-filter-option"
                    onClick={() => handleFilterSelect(tag)}
                  >
                    {tagPhotos[tag] && (
                      <img 
                        src={tagPhotos[tag]} 
                        alt={tag} 
                        className="mobile-filter-option-photo"
                      />
                    )}
                    {tag}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

                {/* Journal Grid */}
        <div className="journal-grid">
          {filteredPosts.map((post) => (
            <article key={post.id} className="journal-card">
              <div className="card-image-container">
                <img 
                  src={post.image} 
                  alt={post.title} 
                  className="card-image"
                />
                <div className="card-tag">{post.tag}</div>
              </div>
              <div className="card-content">
                <h2 className="card-title">{post.title}</h2>
                <p className="card-excerpt">{post.excerpt}</p>
                <button 
                  onClick={() => handlePostClick(post)}
                  className="card-link"
                >
                  Read More
                </button>
              </div>
            </article>
          ))}
        </div>





        {/* Journal CTA */}
        <div className="journal-cta">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Dive Deeper?</h2>
            <p className="cta-text">
              Explore more of my work and creative journey through my gallery and shop.
            </p>
            <div className="cta-buttons">
              <a href="/gallery" className="btn">
                View Gallery
              </a>
              <a href="/shop" className="btn btn-outline">
                Visit Shop
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Journal Modal */}
      <JournalModal 
        post={selectedPost}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default Journal; 